import * as v from 'valibot'

// valibot, not zod, and deliberately: see the note in `company-form.ts`. These are FORM schemas —
// parsed on user input, shipped to a browser — and the server reaches this module only through
// `firstIncompleteProfileStep` below, so one definition still serves both sides.

export const registerSchema = v.object({
  email: v.pipe(v.string(), v.email()),
  password: v.pipe(v.string(), v.minLength(1)),
  termsOfUse: v.literal(true),
})

export const aboutSchema = v.object({
  image: v.union([v.file(), v.undefined(), v.null()]),
  firstname: v.pipe(v.string(), v.minLength(1)),
  lastname: v.pipe(v.string(), v.minLength(1)),
  birthdate: v.pipe(v.string(), v.minLength(1)),
  // Inlined rather than read off `sexSchema` in shared.ts: that is a zod schema, and importing it
  // would pull zod's runtime back into this module and every form chunk that holds it.
  sex: v.picklist(['male', 'female', 'neutral']),
})

export const locationSchema = v.object({
  country: v.pipe(v.string(), v.minLength(1)),
  province: v.pipe(v.string(), v.minLength(1)),
  city: v.pipe(v.string(), v.minLength(1)),
  cp: v.string(),
})

export const personalInfoSchema = v.object({
  ...aboutSchema.entries,
  ...locationSchema.entries,
})

export const jobInfoSchema = v.object({
  job: v.pipe(v.string(), v.minLength(1)),
  // `company == null` alone misses the empty string an input produces once it has been focused and
  // cleared, which let the step submit with a blank company. Treat null/empty/whitespace as missing.
  //
  // `v.forward` attaches the issue to the field it is about, which is what the zod version did with
  // `path: ['company']`. The message is the global "Required" from `form-i18n.ts` — the zod version
  // signalled that with a `params: { i18n: 'required' }` marker on a message-less custom issue; here
  // the check simply carries no message and falls through to the same place.
  work: v.pipe(
    v.object({
      workedPreviously: v.boolean(),
      company: v.nullable(v.string()),
      employees: v.nullable(v.number()),
    }),
    v.forward(
      v.check((value) => !value.workedPreviously || Boolean(value.company?.trim())),
      ['company'],
    ),
    v.forward(
      v.check((value) => !value.workedPreviously || value.employees != null),
      ['employees'],
    ),
  ),
  educationalLevel: v.number(),
  level: v.number(),
  function: v.number(),
  sector: v.number(),
})

export const applicantFormSchema = v.object({
  ...personalInfoSchema.entries,
  ...jobInfoSchema.entries,
})

const _schema = v.object({
  ...registerSchema.entries,
  ...personalInfoSchema.entries,
  ...jobInfoSchema.entries,
})

export type InitialData = Omit<Partial<v.InferInput<typeof _schema>>, 'image'> & { image?: string | null }

// The persisted user fields the profile wizard collects across its steps.
export type ProfileUser = {
  image?: string | null
  firstname?: string | null
  lastname?: string | null
  birthdate?: string | null
  sex?: 'male' | 'female' | 'neutral' | null
  country?: string | null
  province?: string | null
  city?: string | null
  cp?: string | null
  job?: string | null
  company?: string | null
  employees?: number | null
  educationalLevel?: number | null
  level?: number | null
  function?: number | null
  sector?: number | null
}

// First wizard step (in order: about → location → career) whose data doesn't satisfy that step's
// own schema, or null when the profile is complete. Reusing the form schemas keeps the
// "is this step done?" rule in one place — the schemas themselves.
export function firstIncompleteProfileStep(user: ProfileUser): 'about' | 'location' | 'career' | null {
  // `image` is optional in the schema (the saved user has a CDN url, not a File), so omit it.
  if (
    !v.safeParse(aboutSchema, { image: undefined, ...pick(user, 'firstname', 'lastname', 'birthdate', 'sex') }).success
  )
    return 'about'

  if (!v.safeParse(locationSchema, { ...pick(user, 'country', 'province', 'city'), cp: user.cp ?? '' }).success)
    return 'location'

  // The user stores company/employees flat; the wizard nests them under `work` and only requires
  // them when `workedPreviously` is set — which is exactly when a company is on file.
  const career = {
    job: user.job,
    work: { workedPreviously: user.company != null, company: user.company ?? null, employees: user.employees ?? null },
    ...pick(user, 'educationalLevel', 'level', 'function', 'sector'),
  }
  if (!v.safeParse(jobInfoSchema, career).success) return 'career'

  return null
}

function pick<T extends object, K extends keyof T>(obj: T, ...keys: K[]): Pick<T, K> {
  return Object.fromEntries(keys.map((k) => [k, obj[k]])) as Pick<T, K>
}

export type ProfileStep = 'about' | 'location' | 'career'

// Field keys a profile step can render. `image`/`cp` are optional (never force a step); the rest are
// required by the step schemas. `work` is the worked-before toggle + its company/employees fields.
export type ProfileFieldKey =
  | 'image'
  | 'firstname'
  | 'lastname'
  | 'birthdate'
  | 'sex'
  | 'country'
  | 'province'
  | 'city'
  | 'cp'
  | 'job'
  | 'work'
  | 'educationalLevel'
  | 'level'
  | 'function'
  | 'sector'

const isFilled = {
  firstname: (u: ProfileUser) => !!u.firstname?.trim(),
  lastname: (u: ProfileUser) => !!u.lastname?.trim(),
  birthdate: (u: ProfileUser) => !!u.birthdate,
  sex: (u: ProfileUser) => u.sex != null,
  country: (u: ProfileUser) => !!u.country,
  province: (u: ProfileUser) => !!u.province,
  city: (u: ProfileUser) => !!u.city?.trim(),
  job: (u: ProfileUser) => !!u.job?.trim(),
  educationalLevel: (u: ProfileUser) => u.educationalLevel != null,
  level: (u: ProfileUser) => u.level != null,
  function: (u: ProfileUser) => u.function != null,
  sector: (u: ProfileUser) => u.sector != null,
}

/**
 * Which fields each profile step should render for `user`, OMITTING the ones already filled (e.g. by
 * an API/SDK prefill) so the wizard only asks for what's missing. Linked/dependent fields stay
 * together so a partial step still makes sense:
 *  - country + province (the province options depend on the chosen country),
 *  - the worked-before toggle + company/employees + professional level (level's options depend on the
 *    toggle), shown as a unit whenever the level is missing.
 * Optional fields (image, cp) are included only when their step is already shown for a required field.
 * A fully-complete step gets an empty list, so it can be dropped from both the wizard and the indicator.
 */
export function visibleProfileFields(user: ProfileUser): Record<ProfileStep, ProfileFieldKey[]> {
  const about: ProfileFieldKey[] = []
  if (!isFilled.firstname(user)) about.push('firstname')
  if (!isFilled.lastname(user)) about.push('lastname')
  if (!isFilled.birthdate(user)) about.push('birthdate')
  if (!isFilled.sex(user)) about.push('sex')
  if (about.length && !user.image) about.unshift('image') // optional → only when the step already shows

  const location: ProfileFieldKey[] = []
  if (!isFilled.country(user) || !isFilled.province(user)) location.push('country', 'province')
  if (!isFilled.city(user)) location.push('city')
  if (location.length && !user.cp) location.push('cp') // optional → only when the step already shows

  const career: ProfileFieldKey[] = []
  if (!isFilled.level(user)) career.push('work', 'level')
  if (!isFilled.job(user)) career.push('job')
  if (!isFilled.educationalLevel(user)) career.push('educationalLevel')
  if (!isFilled.function(user)) career.push('function')
  if (!isFilled.sector(user)) career.push('sector')

  return { about, location, career }
}

/** Profile steps that still have something to ask (in order) — for the wizard and the step indicator. */
export function neededProfileSteps(user: ProfileUser): ProfileStep[] {
  const fields = visibleProfileFields(user)
  return (['about', 'location', 'career'] as const).filter((step) => fields[step].length > 0)
}
